/*
 * Copyright (c) 2023-2026 Yesan SAS
 * All rights reserved
 *
 * The present software is the confidential and proprietary information of
 * Yesan SAS. You shall not disclose the present software and shall
 * use it only in accordance with the terms of the license agreement you
 * entered into with Yesan SAS. This software may be subject to
 * export or import laws in certain countries.
 */
import React, { useEffect, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from "react-router-dom";
import ReactGA from "react-ga4";

import HUBIntl from 'HUB/satellites/nls/HUBIntl';
import HUBUtils from 'HUB/satellites/utils/HUBUtils';

import YSConfig from 'YS/conf/YSConfig';
import YSNLS from 'YS/nls/YSNLS';

import { useTheme } from 'YS/ui/ThemeProvider.tsx';
//import HomeView from 'YS/ui/HomeView';
//import NotFoundView from 'YS/ui/NotFoundView';

import './App.css';

const HomeView = lazy(() => import('YS/ui/HomeView'));
const ContactView = lazy(() => import('YS/ui/ContactView'));
const YPFView = lazy(() => import('YS/ui/YPFView'));
const YKView = lazy(() => import('YS/ui/YKView'));
const YGView = lazy(() => import('YS/ui/YGView'));
const WLCView = lazy(() => import('YS/ui/WLCView'));
const YFPView = lazy(() => import('YS/ui/YFPView'));
const NotFoundView = lazy(() => import('YS/ui/NotFoundView'));

function App() {
  const { theme, changeTheme } = useTheme();
  const setTheme = () => {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.setAttribute('data-bs-theme', 'dark');
      changeTheme('dark');
    } else {
      document.documentElement.setAttribute('data-bs-theme', 'light');
      changeTheme('light');
    }
  }
  setTheme();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    setTheme();
  });

  useEffect(() => {
    let pn = window.location.pathname;
    let title = HUBIntl.s('Yesan');
    if (!HUBUtils.isEmpty(pn)){
      if (pn.includes("personal-finance")){
        title += ", "+ HUBIntl.s('PersonalFinance');
      } else if (pn.includes("kakeibo")){
        title += ", "+ HUBIntl.s('Kakeibo');
      } else if (pn.includes("genea")){
        title += ", "+ HUBIntl.s('Genea');
      } else if (pn.includes("worklog-calendar-for-jira")){
        title += ", "+ HUBIntl.s('WorklogCalendar');
      } else if (pn.includes("financial-portfolio-for-jira")){
        title += ", "+ HUBIntl.s('FinancialPortfolio');
      } else if (pn.includes("contact")){
        title += ", "+ HUBIntl.s('ContactUs');
      }
    }
    document.title = title;

    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search, title: window.location.pathname + window.location.search });
   }, []);

   const setLocale = (ld) => {
     if (!HUBUtils.isEmpty(ld) && !HUBUtils.isEmpty(ld.params) && !HUBUtils.isEmpty(ld.params.locale) && HUBUtils.inArray(YSNLS.SUPPORTED_LANGS,ld.params.locale)){
       HUBIntl._userLocale=ld.params.locale;
       HUBIntl._userShortLocale=ld.params.locale;
     }
     return null;
   };

   const RedirectUrl = ({ url }) => {
      useEffect(() => {
        window.location.href = url;
      }, [url]);

      return <h5></h5>;
    };

   const router = createBrowserRouter([
    {
      path: "/:locale?",
      element: <HomeView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/contact",
      element: <ContactView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/personal-finance",
      element: <YPFView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/kakeibo",
      element: <YKView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/genea",
      element: <YGView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/worklog-calendar-for-jira",
      element: <WLCView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/financial-portfolio-for-jira",
      element: <YFPView />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/privacy",
      element: <RedirectUrl url={YSConfig.PRIVACY_URL} />,
      loader: setLocale,
      children: []
    },
    {
      path: "/:locale?/support",
      element: <RedirectUrl url={YSConfig.SUPPORT_URL} />,
      loader: setLocale,
      children: []
    },
    {
      path: "*",
      element: <NotFoundView />,
      loader: setLocale,
      children: []
    }
  ]);

  return (
      <RouterProvider router={router} />
  );
}
export default App;
